import { ComponentNames } from "../../constant/component-constant";
import { arraytoObj, cleanEmpty } from "../../helper/sb-fixer";
import { logAdapterEvent, logError } from "../../helper/logger";

const ADAPTER_NAME_NAME = "GenrePackDetailsPanelAdapter";
const PRICE_REGEX = /^\$(\d+)(\.\d+)?$/;

interface IGenrePackDetailsPanelAdapterArgs {
  content: any;
  svodGenreData?: any;
  embeddedSvods?: any;
  epochOffers?: any;
  cmsPageName?: string;
}

// Generated by Copilot- Start - 2026-04-10 11:34:12
interface IResolvedPricing {
  mainPrice: string;
  decimalPrice: string;
  strikePrice: string | null;
  hasEpochData: boolean;
  hasOffer: boolean;
}

/**
 * Parse a formatted price string (e.g. "$14.99") into main + decimal parts.
 */
const parsePriceString = (price: string): { main: string; decimal: string } => {
  const match = price.match(PRICE_REGEX);
  if (match) {
    return {
      main: `$${match[1]}`,
      decimal: match[2] ?? ""
    };
  }
  return {
    main: price,
    decimal: ""
  };
};

const STRIKE_PRICE_PLACEHOLDER = '[strike_through_price]';
const OFFER_DURATION_PLACEHOLDER = '[offer_duration]';
const OFFER_PRICE_PLACEHOLDER = '[offer_price]';

/**
 * Replace [offer_duration] placeholder in a string with the actual promo duration.
 * Returns the original string if no placeholder found, or null if placeholder
 * is present but duration is unavailable.
 */
const resolveOfferDuration = (text: string | null | undefined, promoDuration: number | null): string | null => {
  if (text == null) {
    return null;
  }
  if (!text.includes(OFFER_DURATION_PLACEHOLDER)) {
    return text;
  }
  if (promoDuration == null || promoDuration <= 0) {
    return null;
  } // can't resolve — hide it
  return text.replace(new RegExp(OFFER_DURATION_PLACEHOLDER.replace(/[[\]]/g, '\\$&'), 'g'), String(promoDuration));
};

/**
 * Resolve all known placeholders in legal text:
 *  - [offer_price]           → formatted epoch offer price (e.g. "$69.99")
 *  - [strike_through_price]  → formatted epoch regular price (e.g. "$84.99")
 *  - [offer_duration]        → promo duration in months (e.g. "2")
 *
 * Returns null when any placeholder present cannot be resolved (hides unresolvable text).
 */
const resolveLegalPlaceholders = (
  text: string | null | undefined,
  offerPrice: string | null,
  regularPrice: string | null,
  promoDuration: number | null
): string | null => {
  if (text == null) {
    return null;
  }
  let result = text;

  if (result.includes(OFFER_PRICE_PLACEHOLDER)) {
    if (!offerPrice) {
      return null;
    }
    result = result.replace(new RegExp(OFFER_PRICE_PLACEHOLDER.replace(/[[\]]/g, '\\$&'), 'g'), offerPrice);
  }

  if (result.includes(STRIKE_PRICE_PLACEHOLDER)) {
    if (!regularPrice) {
      return null;
    }
    result = result.replace(new RegExp(STRIKE_PRICE_PLACEHOLDER.replace(/[[\]]/g, '\\$&'), 'g'), regularPrice);
  }

  if (result.includes(OFFER_DURATION_PLACEHOLDER)) {
    if (promoDuration == null || promoDuration <= 0) {
      return null;
    }
    result = result.replace(new RegExp(OFFER_DURATION_PLACEHOLDER.replace(/[[\]]/g, '\\$&'), 'g'), String(promoDuration));
  }

  return result;
};

/**
 * Build a resolved pricing object for a column, preferring epoch data over CMS.
 * When epoch data is available, both main price and decimal/suffix are derived from
 * the epoch value to prevent mismatches between dynamic and static content.
 *
 * hasOffer — true only when epoch price differs from epoch regular price.
 *           Used by the component to conditionally show the offerRibbon.
 *
 * strikePrice — resolved from CMS placeholder [strike_through_price] (replaced with
 *              epoch regular price) or kept as-is when CMS sends a literal value.
 *              Only populated when hasOffer is true.
 */
const resolveColumnPricing = (
  column: any,
  epochPrice: number | string | null,
  epochRegularPrice: number | string | null
): IResolvedPricing => {
  const pricePoint = column?.pricePoint || column?.price || {};
  const cmsPrice = pricePoint?.content || "";
  const cmsSpanText = pricePoint?.spanText?.content || "";
  const cmsStrikePrice = pricePoint?.strikePrice?.content || null;

  if (epochPrice != null) {
    const formattedOffer = typeof epochPrice === "number" ?
      `$${epochPrice.toFixed(2)}` :
      `$${epochPrice}`;
    const parsed = parsePriceString(formattedOffer);
    const decimalSuffix = parsed.decimal ? `${parsed.decimal}/mo.` : "/mo.";

    // Determine if there is a real offer (prices differ)
    const hasOffer = epochRegularPrice != null &&
      Number(epochPrice) !== Number(epochRegularPrice);

    let strikePrice: string | null = null;
    if (hasOffer && epochRegularPrice != null) {
      const formattedRegular = typeof epochRegularPrice === "number" ?
        `$${epochRegularPrice.toFixed(2)}` :
        `$${epochRegularPrice}`;

      if (cmsStrikePrice && cmsStrikePrice.includes(STRIKE_PRICE_PLACEHOLDER)) {
        // CMS uses placeholder — replace with epoch regular price value only
        // (CMS template provides the surrounding format, e.g. "[strike_through_price]/mo.")
        strikePrice = cmsStrikePrice.replace(STRIKE_PRICE_PLACEHOLDER, formattedRegular);
      } else if (cmsStrikePrice) {
        // CMS sends a literal strike price — use as-is
        strikePrice = cmsStrikePrice;
      } else {
        // No CMS strike price at all — generate from epoch data
        strikePrice = `${formattedRegular}/mo.`;
      }
    }

    return {
      mainPrice: parsed.main,
      decimalPrice: decimalSuffix,
      strikePrice,
      hasEpochData: true,
      hasOffer
    };
  }

  // Non-epoch fallback: if CMS has a placeholder we can't resolve, hide it
  let resolvedStrikePrice = cmsStrikePrice;
  if (cmsStrikePrice && cmsStrikePrice.includes(STRIKE_PRICE_PLACEHOLDER)) {
    resolvedStrikePrice = null;
  }

  return {
    mainPrice: cmsPrice,
    decimalPrice: cmsSpanText,
    strikePrice: resolvedStrikePrice,
    hasEpochData: false,
    hasOffer: false
  };
};
// Generated by Copilot- End - 2026-04-10 11:34:12

export const genrePackDetailsPanelAdapter = ({
  content: componentProps,
  svodGenreData: _svodGenreData,
  embeddedSvods: _embeddedSvods,
  epochOffers,
  cmsPageName
}: IGenrePackDetailsPanelAdapterArgs): any => {
  try {
    logAdapterEvent(ADAPTER_NAME_NAME, "Adapting data for Genre Pack Details Panel", {
      cmsPageName
    });
    const content = cleanEmpty(arraytoObj(componentProps));
    const genrePackName = content?.packName;
    const PriceObj = epochOffers?.genreSvodOffers?.offerPrices?.find(
      priceObj => priceObj[genrePackName]
    );

    let epochPrice: number | string | null = null;
    let epochRegularPrice: number | string | null = null;
    let promoDuration: number | null = null;

    if (PriceObj?.[genrePackName]) {
      const { price, regularPrice, promoDuration: pd } = PriceObj[genrePackName];
      promoDuration = pd ?? null;
      const isZero = price === 0 || price === "0" || price === "0.00";
      epochPrice = (!isZero && price) ? price : null;
      epochRegularPrice = regularPrice ?? null;

      // Keep top-level for backward compatibility
      content.offerPrice = epochPrice != null ?
        (typeof epochPrice === "number" ? `$${epochPrice.toFixed(2)}` : `$${epochPrice}`) :
        null;
      content.regularPrice = epochRegularPrice != null ?
        (typeof epochRegularPrice === "number" ? `$${epochRegularPrice.toFixed(2)}` : `$${epochRegularPrice}`) :
        null;
    }

    // Generated by Copilot- Start - 2026-04-10 11:34:12
    // Resolve per-column pricing + legal so the component has no pricing logic
    const hasOffer = epochPrice != null && epochRegularPrice != null &&
      Number(epochPrice) !== Number(epochRegularPrice);

    const columnData = content?.columnData;
    if (Array.isArray(columnData)) {
      content.columnData = columnData.map((column: any) => {
        const hasPricing = column?.pricePoint || column?.price;
        if (!hasPricing) {
          return column;
        }

        const resolved = resolveColumnPricing(column, epochPrice, epochRegularPrice);

        // --- pricePoint.legal: duration label next to price (e.g., "for the first 2 months") ---
        // Resolve CMS pricePoint.legal.content (new CMS format) by replacing [offer_duration]
        // with the epoch promo duration. Only shown when an offer is active.
        const pricePoint = column?.pricePoint || column?.price || {};
        let resolvedLegal: any = null;

        if (resolved.hasOffer && pricePoint?.legal?.content) {
          // CMS has legal content populated — resolve [offer_duration] if present
          resolvedLegal = { ...pricePoint.legal };
          const resolvedContent = resolveOfferDuration(resolvedLegal.content, promoDuration);
          resolvedLegal = resolvedContent ? {
            ...resolvedLegal,
            content: resolvedContent
          } : null;
        }
        // When no offer → resolvedLegal stays null (hidden)

        // --- legalText: pick offer vs no-offer version ---
        // CMS can send legalText (offer version) + legalTextNoOffer (no-offer version)
        // Backward compat: if only legalText exists, use it regardless of offer state
        let resolvedLegalText = column?.legalText || null;
        if (hasOffer) {
          // Prefer the offer version (legalText) — resolve placeholders
          resolvedLegalText = column?.legalText || null;
        } else if (column?.legalTextNoOffer) {
          // No offer + CMS provides a no-offer version — use it
          resolvedLegalText = column.legalTextNoOffer;
        }
        // else: no offer + no legalTextNoOffer — keep legalText as-is (backward compat)

        // Resolve all placeholders ([offer_price], [strike_through_price], [offer_duration]) in legalText
        if (resolvedLegalText?.content) {
          const resolvedContent = resolveLegalPlaceholders(
            resolvedLegalText.content,
            content.offerPrice ?? null,
            content.regularPrice ?? null,
            promoDuration
          );
          if (resolvedContent === null) {
            // Placeholder present but can't resolve — hide it
            resolvedLegalText = null;
          } else {
            resolvedLegalText = {
              ...resolvedLegalText,
              content: resolvedContent
            };
          }
        }

        // Sanitize pricePoint: resolve legal + strip placeholder from strikePrice
        const sanitizedPricePoint = {
          ...pricePoint,
          legal: resolvedLegal,
          ...(resolved.strikePrice != null && pricePoint?.strikePrice && {
            strikePrice: {
              ...pricePoint.strikePrice,
              content: resolved.strikePrice
            }
          })
        };

        return {
          ...column,
          resolvedPricing: resolved,
          pricePoint: sanitizedPricePoint,
          legalText: resolvedLegalText
        };
      });
    }
    // Generated by Copilot- End - 2026-04-10 11:34:12

    return {
      props: {
        ...content,
        ...(content.options && { gnavOptions: content.options }),
        options: {
          id: content.id || null,
          ...(content.options && { ...content.options })
        }
      },
      type: ComponentNames.GENREPACKDETAILSPANEL
    };
  } catch (error) {
    logError(ADAPTER_NAME_NAME, "Error adapting data for Genre Pack Details Panel", {
      error,
      cmsPageName
    });
    return null;
  }
}

export default genrePackDetailsPanelAdapter;
